import React from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, Award, Anchor, MapPin, Clock, Shield, Star } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const teamMembers = [
    {
      name: "Roberto Martínez",
      role: "Fundador y Director",
      description: "Con más de 30 años de experiencia en la industria pesquera patagónica.",
      image: "/images/hero.jpg"
    },
    {
      name: "Elena Martínez",
      role: "Directora de Calidad",
      description: "Especialista en control de calidad y procesos de conservación marina.",
      image: "/images/hero2.jpg"
    },
    {
      name: "Carlos Martínez",
      role: "Director Comercial",
      description: "Segunda generación, especializado en relaciones comerciales y expansión.",
      image: "/images/hero3.jpg"
    }
  ];

  const milestones = [
    {
      year: "1998",
      title: "Los Inicios",
      description: "Roberto Martínez funda Mar de Jade con una pequeña embarcación en Puerto Madryn.",
      icon: <Anchor className="w-6 h-6" />
    },
    {
      year: "2005",
      title: "Expansión Regional",
      description: "Establecemos alianzas con pescadores artesanales de toda la Patagonia.",
      icon: <MapPin className="w-6 h-6" />
    },
    {
      year: "2012",
      title: "Certificación Premium",
      description: "Obtenemos certificaciones internacionales de calidad y sustentabilidad.",
      icon: <Award className="w-6 h-6" />
    },
    {
      year: "2018",
      title: "Nueva Generación",
      description: "La segunda generación se incorpora al negocio familiar con nuevas ideas.",
      icon: <Users className="w-6 h-6" />
    },
    {
      year: "2023",
      title: "Innovación Digital",
      description: "Lanzamos nuestra plataforma digital para acercar el mar a más hogares.",
      icon: <Star className="w-6 h-6" />
    }
  ];

  const values = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Empresa Familiar",
      description: "Tres generaciones trabajando juntas para mantener viva la tradición marina patagónica. Cada miembro de nuestra familia aporta su experiencia y pasión por el mar.",
      gradient: "from-ocean-500 to-jade-500"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Calidad Premium",
      description: "Solo trabajamos with los mejores productos del mar, garantizando frescura y sabor excepcional en cada entrega. Nuestros estándares superan las expectativas más exigentes.",
      gradient: "from-jade-500 to-seagreen-500"
    },
    {
      icon: <Anchor className="w-8 h-8" />,
      title: "Tradición Marina",
      description: "Respetamos y preservamos las técnicas tradicionales de pesca y preparación patagónica, transmitidas de generación en generación por nuestras familias pescadoras.",
      gradient: "from-seagreen-500 to-ocean-500"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Pasión por el Mar",
      description: "Cada producto refleja nuestro amor profundo por el océano y sus regalos naturales. Esta pasión se transmite en cada preparación y servicio que ofrecemos.",
      gradient: "from-ocean-500 to-deep-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Sustentabilidad",
      description: "Nos comprometemos con prácticas pesqueras responsables que protegen el ecosistema marino para las futuras generaciones de nuestra familia y la tuya.",
      gradient: "from-deep-500 to-jade-500"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Compromiso",
      description: "25 años de experiencia nos respaldan. Nuestro compromiso es entregar productos frescos, auténticos y de la más alta calidad en cada pedido.",
      gradient: "from-jade-500 to-ocean-500"
    }
  ];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-pearl-50/50 via-white to-ocean-25/30">
        
        {/* Hero Section */}
        <motion.div
          className="relative py-20 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto text-center">
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-transparent bg-gradient-to-r from-ocean-600 to-jade-600 bg-clip-text mb-6 font-heading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Nuestra Historia
            </motion.h1>
            
            <motion.p 
              className="text-xl sm:text-2xl text-deep-600 max-w-4xl mx-auto leading-relaxed mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Somos una empresa familiar con más de 25 años de experiencia, 
              dedicada a traer lo mejor del mar patagónico directamente a tu hogar. 
              Nuestra pasión por el océano y el compromiso con la calidad nos han 
              convertido en referentes del sector.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/productos">
                <Button size="lg" className="bg-gradient-to-r from-ocean-600 to-jade-600 hover:from-ocean-700 hover:to-jade-700 text-white px-8 py-3 rounded-full text-lg font-semibold shadow-lg">
                  Ver Nuestros Productos
                </Button>
              </Link>
              <Link to="/servicios">
                <Button variant="outline" size="lg" className="border-2 border-ocean-600 text-ocean-600 hover:bg-ocean-600 hover:text-white px-8 py-3 rounded-full text-lg font-semibold">
                  Conoce Nuestros Servicios
                </Button>
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Main Story Section */}
        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
              
              {/* Story Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-deep-700 mb-8 font-heading">
                  De Generación en Generación
                </h2>
                
                <div className="space-y-6">
                  <p className="text-lg text-deep-600 leading-relaxed">
                    Todo comenzó en 1998 cuando Roberto Martínez, un apasionado pescador de Puerto Madryn, 
                    decidió compartir su amor por los frutos del mar patagónico con más familias argentinas. 
                    Con una pequeña embarcación y un gran sueño, nació Mar de Jade.
                  </p>
                  
                  <p className="text-lg text-deep-600 leading-relaxed">
                    Durante más de dos décadas, hemos construido relaciones sólidas con pescadores artesanales 
                    a lo largo de toda la costa patagónica. Desde las frías aguas de Tierra del Fuego hasta 
                    las bahías de Península Valdés, cada producto que ofrecemos cuenta una historia de 
                    tradición, esfuerzo y respeto por el mar.
                  </p>
                  
                  <p className="text-lg text-deep-600 leading-relaxed">
                    Hoy, con la segunda generación ya integrada al negocio familiar, continuamos innovando 
                    sin perder la esencia que nos caracteriza: la búsqueda incansable de la excelencia y 
                    el compromiso genuino con nuestros clientes, a quienes consideramos parte de nuestra 
                    gran familia marina.
                  </p>
                </div>
              </motion.div>

              {/* Hero Image */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="relative h-96 lg:h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/images/hero.jpg"
                    alt="Mar de Jade - Historia familiar"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-deep-900/50 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl font-bold text-white mb-2">25 Años de Tradición</h3>
                    <p className="text-ocean-100">Conectando el mar patagónico con tu mesa desde 1998</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-ocean-50 via-pearl-50 to-jade-50" />
          <div className="absolute inset-0 bg-white/80" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-transparent bg-gradient-to-r from-ocean-600 to-jade-600 bg-clip-text mb-6 font-heading">
                Nuestro Recorrido
              </h2>
              <p className="text-xl text-deep-600 max-w-3xl mx-auto">
                Los hitos más importantes que han marcado nuestra historia y crecimiento como empresa familiar
              </p>
            </motion.div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex flex-col lg:flex-row items-center gap-8 ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className="flex-1 max-w-xl">
                    <Card className="p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl rounded-3xl">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`p-3 bg-gradient-to-r ${
                          index % 3 === 0 ? 'from-ocean-500 to-jade-500' : 
                          index % 3 === 1 ? 'from-jade-500 to-seagreen-500' : 
                          'from-seagreen-500 to-ocean-500'
                        } rounded-xl text-white`}>
                          {milestone.icon}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-deep-700">{milestone.title}</h3>
                          <p className="text-ocean-600 font-semibold">{milestone.year}</p>
                        </div>
                      </div>
                      <p className="text-lg text-deep-600 leading-relaxed">
                        {milestone.description}
                      </p>
                    </Card>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <div className="w-4 h-4 bg-gradient-to-r from-ocean-500 to-jade-500 rounded-full shadow-lg"></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-transparent bg-gradient-to-r from-ocean-600 to-jade-600 bg-clip-text mb-6 font-heading">
                Nuestros Valores
              </h2>
              <p className="text-xl text-deep-600 max-w-3xl mx-auto">
                Los principios que guían cada decisión y acción en Mar de Jade
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="group"
                >
                  <Card className="h-full p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-500 rounded-3xl">
                    <div className="flex flex-col items-center text-center space-y-6">
                      <div className={`p-4 bg-gradient-to-r ${value.gradient} rounded-2xl text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        {value.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-deep-700">{value.title}</h3>
                      <p className="text-deep-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-ocean-50 via-pearl-50 to-jade-50" />
          <div className="absolute inset-0 bg-white/80" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-transparent bg-gradient-to-r from-ocean-600 to-jade-600 bg-clip-text mb-6 font-heading">
                Nuestra Familia
              </h2>
              <p className="text-xl text-deep-600 max-w-3xl mx-auto">
                Conoce a las personas que hacen posible que Mar de Jade siga creciendo
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="group"
                >
                  <Card className="overflow-hidden bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-500 rounded-3xl">
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-deep-900/60 via-transparent to-transparent" />
                    </div>
                    <div className="p-8 text-center">
                      <h3 className="text-2xl font-bold text-deep-700 mb-2">{member.name}</h3>
                      <p className="text-ocean-600 font-semibold mb-4">{member.role}</p>
                      <p className="text-deep-600 leading-relaxed">{member.description}</p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center bg-gradient-to-r from-ocean-600 to-jade-600 rounded-3xl p-12 text-white"
            >
              <h3 className="text-3xl sm:text-4xl font-bold mb-6 font-heading">
                Únete a Nuestra Familia Marina
              </h3>
              <p className="text-xl mb-8 text-ocean-100 max-w-3xl mx-auto leading-relaxed">
                Descubre por qué miles de familias ya confían en Mar de Jade para llevar 
                lo mejor del mar patagónico a sus hogares. Forma parte de nuestra historia.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/productos">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white text-ocean-600 px-8 py-3 rounded-full font-semibold text-lg hover:bg-pearl-50 transition-colors duration-300 shadow-lg"
                  >
                    Ver Productos
                  </motion.button>
                </Link>
                <a href="#contacto">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold text-lg hover:bg-white hover:text-ocean-600 transition-colors duration-300"
                  >
                    Contáctanos
                  </motion.button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default AboutPage;
